/**
 * Payer Connections — the per-payer fold (AC-3, AC-4, AC-6, AC-8).
 *
 * Pure functions. No API calls, no side effects, no Date.now() — callers
 * that need "today" (e.g. the roster's days-blocked calculation) pass an
 * explicit `asOf`.
 */

import { resolveWhatsHappening } from './copy';
import {
  type EnrollmentRow,
  type EnrollmentStatus,
  type FoldedPayer,
  type FoldResult,
  type PayerState,
  type RailView,
  SEVERITY_ORDER,
} from './types';

/**
 * Severity rank — higher is more severe. Derived from SEVERITY_ORDER (not
 * hand-duplicated) so `mostSevere`/`pickWhatsHappeningRail` and
 * `sortPayers`/`summaryChipCounts` can never disagree about ordering.
 */
const SEVERITY_RANK = Object.fromEntries(
  SEVERITY_ORDER.map((state, index) => [state, SEVERITY_ORDER.length - 1 - index]),
) as Record<Exclude<PayerState, 'live'>, number>;

/**
 * Fold one rail's raw Stedi status to a state, per AC-3.
 * DRAFT/CANCELED -> needs_us. PROVIDER_ACTION_REQUIRED/REJECTED -> needs_customer.
 * STEDI_ACTION_REQUIRED/PROVISIONING/unknown -> waiting. LIVE -> ready.
 */
export function foldStatus(status: EnrollmentStatus): Exclude<PayerState, 'live'> {
  switch (status) {
    case 'DRAFT':
    case 'CANCELED':
      return 'needs_us';
    case 'PROVIDER_ACTION_REQUIRED':
    case 'REJECTED':
      return 'needs_customer';
    case 'LIVE':
      return 'ready';
    case 'STEDI_ACTION_REQUIRED':
    case 'PROVISIONING':
    default:
      return 'waiting';
  }
}

/** A payer's state is its most severe rail's state (AC-3). */
function mostSevere(states: Array<Exclude<PayerState, 'live'>>): Exclude<PayerState, 'live'> {
  return states.reduce(
    (worst, s) => (SEVERITY_RANK[s] > SEVERITY_RANK[worst] ? s : worst),
    'ready',
  );
}

/**
 * Group rows by payer. Grouping key is `stedi_payer_id`, falling back to
 * `payer_name` when missing/empty. Two distinct payers that both lack an id
 * and share a name would collide into one fold under this fallback — Stedi
 * normally populates the id, so this is a documented edge case, not a guard.
 */
function groupKey(row: EnrollmentRow): string {
  return row.stedi_payer_id || row.payer_name;
}

interface PayerGroup {
  payerId: string;
  payerName: string;
  rows: EnrollmentRow[];
}

function groupByPayer(rows: EnrollmentRow[]): PayerGroup[] {
  const groups = new Map<string, PayerGroup>();
  for (const row of rows) {
    const key = groupKey(row);
    const existing = groups.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      groups.set(key, { payerId: key, payerName: row.payer_name, rows: [row] });
    }
  }
  return [...groups.values()];
}

/**
 * "What's happening" for a payer is the most-severe rail's message (AC-6).
 * A tie in severity is broken by rail order: 837P before 835 before anything else.
 */
const RAIL_TIEBREAK_ORDER = ['837P', '835'];

function pickWhatsHappeningRail(rails: RailView[]): RailView {
  return rails.slice().sort((a, b) => {
    const severityDiff = SEVERITY_RANK[b.state] - SEVERITY_RANK[a.state];
    if (severityDiff !== 0) return severityDiff;
    const aIdx = RAIL_TIEBREAK_ORDER.indexOf(a.transactionType);
    const bIdx = RAIL_TIEBREAK_ORDER.indexOf(b.transactionType);
    return (
      (aIdx === -1 ? RAIL_TIEBREAK_ORDER.length : aIdx) -
      (bIdx === -1 ? RAIL_TIEBREAK_ORDER.length : bIdx)
    );
  })[0];
}

/**
 * Fold one organization's enrollment rows to per-payer state, per AC-3, AC-4, AC-6, AC-8.
 *
 * Live determination (AC-4, corrected 2026-09-04 — the PRD's fold diagram
 * and the design handoff both gate Live on the payer's *folded* state, not
 * on the 837P rail alone; AC-4's prose had drifted from them and has been
 * fixed): a payer is `live` when its folded state is `ready` — i.e. every
 * rail it has is LIVE — AND the org has one or more submitted claims
 * (org-wide count, per Q9). A payer whose 837P rail is LIVE but whose 835
 * rail disagrees (e.g. REJECTED or still PROVISIONING) is NOT live; it
 * keeps its folded state (needs_customer/waiting) and stays in the row
 * list. A payer with only an 837P rail — no 835 enrollment at all — is
 * still eligible once that rail is LIVE. See the "two rails disagree" and
 * "837P-only" fixtures in __tests__/fold.test.ts.
 *
 * Live payers are excluded from `payers` and rolled into `liveCount`.
 */
export function foldPayers(rows: EnrollmentRow[], submittedClaimCount: number): FoldResult {
  const groups = groupByPayer(rows);

  const counts: Record<PayerState, number> = {
    needs_us: 0,
    needs_customer: 0,
    waiting: 0,
    ready: 0,
    live: 0,
  };

  const payers: FoldedPayer[] = [];

  for (const group of groups) {
    const rails: RailView[] = group.rows.map(row => {
      const state = foldStatus(row.status);
      return {
        transactionType: row.transaction_type,
        status: row.status,
        state,
        statusMessage: row.status_message,
        rejectionReason: row.rejection_reason,
        submittedAt: row.submitted_at,
        createdAt: row.created_at,
        portalUrl: row.portal_url,
      };
    });

    const foldedState = mostSevere(rails.map(r => r.state));

    const isLive = foldedState === 'ready' && submittedClaimCount >= 1;
    const finalState: PayerState = isLive ? 'live' : foldedState;

    counts[finalState] += 1;

    if (finalState === 'live') continue;

    const whatsHappeningRail = pickWhatsHappeningRail(rails);
    const railsByType: Partial<Record<string, RailView>> = {};
    for (const rail of rails) railsByType[rail.transactionType] = rail;

    payers.push({
      payerId: group.payerId,
      payerName: group.payerName,
      state: finalState,
      rails: railsByType,
      whatsHappening: resolveWhatsHappening(
        whatsHappeningRail.status,
        whatsHappeningRail.statusMessage,
        whatsHappeningRail.rejectionReason,
      ),
      portalUrl: whatsHappeningRail.portalUrl,
    });
  }

  payers.sort(sortPayers);

  const enrollingCount = payers.length;
  const liveCount = counts.live;

  return { payers, counts, enrollingCount, liveCount };
}

/** AC-8: sort by state severity, then payer name. */
export function sortPayers(a: FoldedPayer, b: FoldedPayer): number {
  const severityDiff = SEVERITY_ORDER.indexOf(a.state) - SEVERITY_ORDER.indexOf(b.state);
  if (severityDiff !== 0) return severityDiff;
  return a.payerName.localeCompare(b.payerName);
}

/** AC-8: one summary chip per non-zero state, in severity order. */
export function summaryChipCounts(
  counts: Record<PayerState, number>,
): Array<{ state: (typeof SEVERITY_ORDER)[number]; count: number }> {
  return SEVERITY_ORDER.filter(state => counts[state] > 0).map(state => ({
    state,
    count: counts[state],
  }));
}
